import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { Trash2, X, Loader2, Image as ImageIcon } from 'lucide-react';
import { notesApi } from '@/lib/notesApi';
import { extractError } from '@/lib/errors';
import { tagThemeClass } from './tagTheme';

// Alt + D with pieces selected: confirm, then delete the selected tags.
export function TagDeleteSelected({ pieces, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const k = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [busy, onClose]);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await notesApi.post('/notes/tag-workspace/delete-tags', { ids: pieces.map((p) => p.id) });
      const n = data.deleted + data.hidden;
      toast.success(`${n} tag${n === 1 ? '' : 's'} deleted.`);
      onDeleted(pieces.map((p) => p.id));
    } catch (e) { toast.error(extractError(e, 'Could not delete the tags.')); setBusy(false); }
  };
  return createPortal(
    <div className={`tag-scope ${tagThemeClass()} fixed inset-0 z-[400] flex items-end justify-center sm:items-center sm:p-3`} data-testid="tagws-delete-selected-dialog">
      <div className="absolute inset-0 bg-black/55 backdrop-blur-[2px]" onClick={() => !busy && onClose()} />
      <div className="relative z-10 flex max-h-[85vh] w-full flex-col overflow-hidden rounded-t-3xl border border-border bg-card shadow-2xl animate-in fade-in slide-in-from-bottom-2 duration-200 sm:max-w-md sm:rounded-2xl">
        <div className="flex items-start justify-between gap-3 bg-destructive/10 px-5 pb-4 pt-5">
          <div className="flex items-start gap-3">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-destructive text-destructive-foreground"><Trash2 size={19} /></span>
            <div>
              <h3 className="font-semibold text-foreground">Delete {pieces.length} selected tag{pieces.length === 1 ? '' : 's'}?</h3>
              <p className="text-xs text-muted-foreground" data-testid="tagws-delete-selected-message">{pieces.length} selected tag{pieces.length === 1 ? '' : 's'} will get deleted and then they cannot be recovered.</p>
            </div>
          </div>
          <button onClick={onClose} disabled={busy} aria-label="Close" data-testid="tagws-delete-selected-close"><X size={18} /></button>
        </div>
        <div className="min-h-0 flex-1 divide-y divide-border/60 overflow-y-auto px-5">
          {pieces.map((p) => (
            <div key={p.id} className="flex items-center gap-3 py-2" data-testid="tagws-delete-selected-piece">
              <span className="grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-lg border border-border bg-secondary/50">{p.thumb ? <img src={p.thumb} alt="" className="h-full w-full object-contain" /> : <ImageIcon size={14} className="text-muted-foreground" />}</span>
              <span className="truncate font-mono text-sm font-bold text-foreground">{p.jewellery_no}</span>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-2 border-t border-border p-4">
          <button type="button" onClick={onClose} disabled={busy} className="h-10 rounded-xl border border-border text-sm font-semibold text-foreground hover:bg-secondary" data-testid="tagws-delete-selected-cancel">Cancel</button>
          <button type="button" onClick={run} disabled={busy} className="inline-flex h-10 items-center justify-center gap-1.5 rounded-xl bg-destructive text-sm font-semibold text-destructive-foreground disabled:opacity-60" data-testid="tagws-delete-selected-confirm">{busy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Delete</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
